import React, { ReactNode, useEffect } from "react";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { Pay, IPayFlowRoutes } from "@modules/PaymentProcessing/Flows/Pay/Pay";
import { useAvailablePaymentMethods } from "@modules/PaymentProcessing/hooks/useAvailablePaymentMethods";
import { Box } from "@UI/Components/Box/Box";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { CheckOrder } from "./CheckOrder/CheckOrder";
import { createFlow } from "@Libs/Flow/services/createFlow";
import { withFlow } from "@Libs/Flow/hocs/withFlow";
import { useFlow } from "@Libs/Flow/hooks/useFlow";
import { isEmpty } from "lodash-es";
import {
  PaymentMethodProviderCombination,
  PaymentMethodType,
} from "@modules/PaymentProcessing/types";

export type ICheckoutFlowRoutes = "CHOOSE_PLAN" | IPayFlowRoutes;

export const CheckoutFlow = createFlow<
  PaymentMethodProviderCombination<PaymentMethodType> & {
    isPaymentConfirmed?: boolean;
  },
  {
    paymentFormMessage?: ReactNode;
    basketId?: string;
    relatedServiceId?: number;
  }
>({
  root: "CHECKOUT",
});

export const Checkout = withFlow(CheckoutFlow, () => {
  const {
    rootRoute,
    onSuccess,
    onLeave,
    onPageMount,
    state: { paymentFormMessage, basketId, relatedServiceId },
  } = useFlow(CheckoutFlow);
  const paramsBasketId = useSearchParams("resourceId") ?? basketId;
  const {
    currentBasket,
    isLoading: isCurrentBasketLoading,
    setCurrentBasketId,
    currentBasketId,
  } = useCurrentBasket();

  const {
    enabledPaymentMethods,
    allowedPaymentMethods,
    isLoading: isAvailablePaymentMethodsLoading,
  } = useAvailablePaymentMethods({
    resourceType: "basket",
    resourceAttributes: { id: currentBasketId, items: currentBasket?.items },
  });

  const isBasketFromParamsLoading =
    !!(paramsBasketId && currentBasketId !== paramsBasketId) ||
    !currentBasketId;

  useEffect(() => {
    if (paramsBasketId) {
      setCurrentBasketId(paramsBasketId);
    }
  }, [paramsBasketId]);

  useEffect(() => {
    if (
      !isCurrentBasketLoading &&
      !paramsBasketId &&
      !isBasketFromParamsLoading &&
      currentBasketId &&
      isEmpty(currentBasket?.items)
    ) {
      onLeave();
    }
  }, [isCurrentBasketLoading, currentBasket?.items, paramsBasketId]);

  if (
    isCurrentBasketLoading ||
    isAvailablePaymentMethodsLoading ||
    isBasketFromParamsLoading
  ) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Routes>
        <Route
          fragments="ROOT"
          element={<CheckOrder allowedPaymentMethods={allowedPaymentMethods} />}
          matchAny
        />
        <Route
          fragments={Pay.root}
          matchAny
          element={
            <Pay
              onSuccess={onSuccess}
              backRoute={rootRoute}
              onLeave={onLeave}
              onPageMount={onPageMount}
              state={{
                allowedPaymentMethods,
                enabledPaymentMethods,
                resourceId: currentBasketId,
                resourceType: "basket",
                relatedServiceId,
                paymentFormMessage,
              }}
            />
          }
        />
      </Routes>
    </Box>
  );
});
